import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';
import ReactDOM from 'react-dom';
import { useTheme, ThemeProvider } from 'styled-components';

import { Icon } from '../icon';

import {
  ToastContainer as ToastContainerStyled,
  ToastStack,
  ToastItem,
  ToastContent,
  ToastMessage,
  ToastTitle,
  ToastDescription,
  ToastCloseButton,
  ToastMoreButton,
  ToastActionButtons,
  ToastCloseAllButton,
} from './styles';
import { ToastType, ToastPosition } from './toast-theme';

import { ICONS } from '@controlrooms/constants';

export interface Toast {
  id: string;
  type: ToastType;
  message: string | ReactNode;
  title?: string;
  description?: string | ReactNode;
  autoclose?: boolean;
  autocloseTime?: number;
  position?: ToastPosition;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  closeAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  maxVisible?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, maxVisible = 3 }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [expandedPositions, setExpandedPositions] = useState<Set<string>>(new Set());
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newToast: Toast = {
        ...toast,
        id,
        autoclose: toast.autoclose ?? true,
        autocloseTime: toast.autocloseTime ?? 500000,
        position: toast.position ?? 'top-right',
      };

      setToasts((prev) => [...prev, newToast]);

      // Auto-close logic
      if (newToast.autoclose && newToast.autocloseTime) {
        const timeout = setTimeout(() => {
          removeToast(id);
        }, newToast.autocloseTime);
        timeoutRefs.current.set(id, timeout);
      }

      return id;
    },
    [removeToast],
  );

  const clearToasts = useCallback(() => {
    setToasts([]);
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current.clear();
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };
  }, []);

  const closeAll = useCallback(() => {
    setToasts([]);
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current.clear();
  }, []);

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    closeAll,
  };

  // Group toasts by position
  const toastsByPosition = toasts.reduce((acc, toast) => {
    const position = toast.position || 'top-right';
    if (!acc[position]) {
      acc[position] = [];
    }
    acc[position].push(toast);
    return acc;
  }, {} as Record<ToastPosition, Toast[]>);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <ToastContainer
          key={position}
          position={position as ToastPosition}
          toasts={positionToasts}
          maxVisible={maxVisible}
          isExpanded={expandedPositions.has(position)}
          onExpand={() => {
            setExpandedPositions((prev) => new Set(prev).add(position));
          }}
          onRemove={removeToast}
          onCloseAll={clearToasts}
        />
      ))}
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  position: ToastPosition;
  toasts: Toast[];
  maxVisible: number;
  isExpanded: boolean;
  onExpand: () => void;
  onRemove: (id: string) => void;
  onCloseAll: () => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  position,
  toasts,
  maxVisible,
  isExpanded,
  onExpand,
  onRemove,
  onCloseAll,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Get theme from nearest ThemeProvider
  const theme = useTheme();

  // Get visible toasts (last maxVisible)
  const visibleToasts = toasts.slice(-maxVisible);
  const hiddenCount = Math.max(0, toasts.length - maxVisible);

  // When expanded, show all toasts; otherwise show only visible ones
  const toastsToShow = isExpanded ? toasts : visibleToasts;

  const getIconName = (type: ToastType): string => {
    switch (type) {
      case 'success':
        return ICONS.Checkmark;
      case 'warning':
        return ICONS.Alert;
      case 'error':
        return ICONS.SystemDown;
      case 'info':
        return ICONS.Info;
      default:
        return ICONS.Info;
    }
  };

  if (toasts.length === 0) {
    return null;
  }

  const container = (
    <ThemeProvider theme={theme}>
      <ToastContainerStyled ref={containerRef} position={position} isHovered={false}>
        <ToastStack>
          {toastsToShow.map((toast, index) => (
            <Toast
              key={toast.id}
              toast={toast}
              index={index}
              totalVisible={toastsToShow.length}
              isExpanded={isExpanded}
              isVisible={true}
              onRemove={onRemove}
              getIconName={getIconName}
            />
          ))}
          {!isExpanded && hiddenCount > 0 && (
            <ToastActionButtons>
              <ToastMoreButton
                type="default"
                isVisible={true}
                isExpanded={false}
                index={toastsToShow.length}
                totalVisible={toastsToShow.length + 1}
                onClick={onExpand}
              >
                +{hiddenCount} more
              </ToastMoreButton>
              <ToastCloseAllButton onClick={onCloseAll} aria-label="Close all toasts" type="button">
                <Icon name={ICONS.CloseX} width="10" height="10" />
              </ToastCloseAllButton>
            </ToastActionButtons>
          )}
        </ToastStack>
      </ToastContainerStyled>
    </ThemeProvider>
  );

  return ReactDOM.createPortal(container, document.body);
};

interface ToastProps {
  toast: Toast;
  index: number;
  totalVisible: number;
  isExpanded: boolean;
  isVisible: boolean;
  onRemove: (id: string) => void;
  getIconName: (type: ToastType) => string;
}

const Toast: React.FC<ToastProps> = ({
  toast,
  index,
  totalVisible,
  isExpanded,
  isVisible,
  onRemove,
  getIconName,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 200); // Match animation duration
  };

  if (!isVisible) {
    return null;
  }

  // Render message content
  // If title/description are provided, use those; otherwise use message
  const hasTitleOrDescription = toast.title || toast.description;
  const messageContent = hasTitleOrDescription ? (
    <>
      {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
      {toast.description && (
        <ToastDescription>
          {typeof toast.description === 'string' ? toast.description : toast.description}
        </ToastDescription>
      )}
    </>
  ) : typeof toast.message === 'string' ? (
    <ToastTitle>{toast.message}</ToastTitle>
  ) : (
    <ToastDescription>{toast.message}</ToastDescription>
  );

  return (
    <ToastItem
      type={toast.type}
      isVisible={isVisible}
      isExpanded={isExpanded}
      index={index}
      totalVisible={totalVisible}
      className={isExiting ? 'exiting' : ''}
    >
      <ToastContent>
        <ToastMessage>{messageContent}</ToastMessage>
      </ToastContent>
      <ToastCloseButton onClick={handleClose} aria-label="Close toast" type="button">
        <Icon name={ICONS.CloseX} width="10" height="10" />
      </ToastCloseButton>
    </ToastItem>
  );
};
